import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutGrid, LogOut, Menu, ScrollText, Shield, SlidersHorizontal, User, Users, X } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { BrandMark } from './BrandMark';
import { PlatformLauncherWidget } from './PlatformLauncherWidget';
import { PlatformDataAreaLaunchModalHost } from './PlatformDataAreaLaunchModalHost';
import { pageVariants, pageTransition } from '../portal/portalConfig';
import { prefetchJsonCached } from '../utils/portalQueryCache';

function resolveAndRedirect(target) {
  if (!target) {
    return false;
  }

  if (target.startsWith('http://') || target.startsWith('https://')) {
    window.location.assign(target);
    return true;
  }

  return false;
}

function isHkSupportRole(user) {
  return String(user?.roleCode || '').toUpperCase() === 'HK_SUPPORT';
}

function isSupportRole(user) {
  return String(user?.roleCode || '').toUpperCase() === 'SUPPORT';
}

function isSalesStaffRole(role) {
  return role === 'Sales Staff';
}

function isMasterSalesStaffRole(user) {
  const role = user?.role;
  return role === 'Master Sales Staff'
    || (Array.isArray(user?.masterSalesStaffStoreIds) && user.masterSalesStaffStoreIds.length > 0);
}

const navActiveSpring = { type: 'spring', stiffness: 420, damping: 34, mass: 0.82 };

function SidebarNavLink({ item, layoutId, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
      onClick={onNavigate}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId={layoutId}
              className="nav-item-active-bg"
              transition={navActiveSpring}
              aria-hidden="true"
            />
          )}
          <item.icon size={16} />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function ShellLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [avatarError, setAvatarError] = React.useState(false);

  React.useEffect(() => {
    setAvatarError(false);
  }, [user?.avatarUrl]);

  React.useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    prefetchJsonCached('/api/profile', { ttlMs: 120_000, signal: controller.signal });
    prefetchJsonCached('/api/tools', { ttlMs: 60_000, signal: controller.signal });

    const isSalesStaff = isSalesStaffRole(user.role);
    const isSupport = isSupportRole(user);
    const isMasterSalesStaff = isMasterSalesStaffRole(user);
    const canManageUsers =
      !isSalesStaff
      && !isSupport
      && (
        isHkSupportRole(user)
        || user.role === 'Customer Master'
        || user.role === 'Group-Admin'
        || user.role === 'Store-Admin'
        || isMasterSalesStaff
        || (Array.isArray(user.groupAdminGroupIds) && user.groupAdminGroupIds.length > 0)
        || (Array.isArray(user.storeAdminStoreIds) && user.storeAdminStoreIds.length > 0)
        || (user.role === 'Customer Master' && Array.isArray(user.customerMasterGroupIds) && user.customerMasterGroupIds.length > 0)
      );

    if (canManageUsers) {
      prefetchJsonCached('/api/admin/roles/options', { ttlMs: 300_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/users', { ttlMs: 30_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/groups', { ttlMs: 300_000, signal: controller.signal });
      prefetchJsonCached('/api/admin/roles/stores', { ttlMs: 300_000, signal: controller.signal });
    }

    if (isHkSupportRole(user)) {
      prefetchJsonCached('/api/admin/logs?page=1&pageSize=20', { ttlMs: 30_000, signal: controller.signal });
    }

    return () => controller.abort();
  }, [user]);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const onLogout = React.useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      const redirectUrl = await logout('/login');
      if (!redirectUrl) {
        return;
      }

      if (resolveAndRedirect(redirectUrl)) {
        return;
      }

      if (String(redirectUrl).startsWith('/api/auth/')) {
        window.location.assign(redirectUrl);
        return;
      }

      navigate(redirectUrl || '/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, logout, navigate]);

  const navItems = React.useMemo(() => {
    const role = user?.role;
    const isSalesStaff = isSalesStaffRole(role);
    const isSupport = isSupportRole(user);
    const isMasterSalesStaff = isMasterSalesStaffRole(user);
    const canManageUsers = !isSalesStaff && !isSupport && (
      isHkSupportRole(user)
      || role === 'Customer Master'
      || role === 'Group-Admin'
      || role === 'Store-Admin'
      || isMasterSalesStaff
      || (Array.isArray(user?.groupAdminGroupIds) && user.groupAdminGroupIds.length > 0)
      || (Array.isArray(user?.storeAdminStoreIds) && user.storeAdminStoreIds.length > 0)
    );
    const canManagePlatformAccess = canManageUsers && !isMasterSalesStaff;

    return [
      { to: '/platforms', label: 'Platforms', icon: LayoutGrid },
      { to: '/profile', label: 'Profile', icon: User },
      ...(canManageUsers ? [{ to: '/users', label: 'Users', icon: Users }] : []),
      ...(canManagePlatformAccess ? [{ to: '/platform-access', label: 'Platform Access', icon: SlidersHorizontal }] : []),
      ...(isHkSupportRole(user) ? [{ to: '/logs', label: 'Logs', icon: ScrollText }] : []),
      ...(isHkSupportRole(user) ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
    ];
  }, [user]);

  return (
    <div className="portal-shell">
      <aside className="portal-sidebar">
        <div>
          <BrandMark variant="sidebar" />

          <nav className="portal-nav">
            {navItems.map((item) => (
              <SidebarNavLink key={item.to} item={item} layoutId="sidebar-nav-active" />
            ))}
          </nav>
        </div>

        <motion.button type="button" className="ghost-button logout-button" onClick={onLogout} whileTap={{ scale: 0.97 }} disabled={isLoggingOut}>
          <LogOut size={16} />
          <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
        </motion.button>
      </aside>

      <main className="portal-content">
        <div className="mobile-topbar">
          <Link to="/platforms" className="mobile-topbar-left brand-home-link brand-home-link-wordmark" aria-label="HK ID — go to Platforms">
            <img className="mobile-topbar-wordmark" src="/hk-id-logo.png" alt="HK ID" />
          </Link>
          <button
            type="button"
            className="mobile-burger-button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation menu"
          >
            <Menu size={18} />
          </button>
        </div>

        <header className="content-header">
          <div className="content-header-user">
            {user?.avatarUrl && !avatarError ? (
              <img
                className="content-header-avatar-image"
                src={user.avatarUrl}
                alt="Profile"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="content-header-avatar-fallback">
                {(user?.displayName || user?.email || 'HK').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
            <h1>Welcome, {user?.displayName ?? 'User'}</h1>
            <p>{user?.email}</p>
            </div>
          </div>
          <div className="content-header-actions">
            <motion.span
              key={user?.role ?? 'User'}
              className="role-pill"
              initial={{ opacity: 0, y: -6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              {user?.role ?? 'User'}
            </motion.span>
            <PlatformLauncherWidget />
          </div>
        </header>

        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {mobileNavOpen && (
          <motion.div
            className="mobile-nav-overlay"
            onClick={() => setMobileNavOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.aside
              className="mobile-nav-drawer"
              onClick={(event) => event.stopPropagation()}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mobile-nav-header">
                <BrandMark variant="sidebar" />
                <button
                  type="button"
                  className="mobile-burger-button"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation menu"
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="portal-nav mobile-nav-list">
                {navItems.map((item) => (
                  <SidebarNavLink
                    key={item.to}
                    item={item}
                    layoutId="mobile-nav-active"
                    onNavigate={() => setMobileNavOpen(false)}
                  />
                ))}
              </nav>

              <button type="button" className="ghost-button logout-button mobile-logout-button" onClick={onLogout} disabled={isLoggingOut}>
                <LogOut size={16} />
                <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              </button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
      <PlatformDataAreaLaunchModalHost />
    </div>
  );
}
